// Skin #0 (Default T) and #1 (Default CT) builders.

import * as THREE from 'three';
import {
  SEG, darken, mat, matSkin, enableShadows, fppHeadGroup, buildHumanoid,
} from './skin-utils';

/* ------------------------------------------------------------------ */
/*  #0 — Default T: Military soldier with balaclava                    */
/* ------------------------------------------------------------------ */

export function buildDefaultT(color: number): THREE.Group {
  const group = new THREE.Group();
  const headwearColor = darken(color, 0.4);
  const vestColor = darken(color, 0.85);
  const bootColor = darken(color, 0.3);
  const skinColor = 0xc8a882;

  const body = buildHumanoid({
    bodyColor: color,
    headColor: headwearColor,
    neckColor: headwearColor,
    bootColor,
    handColor: bootColor,
    showFace: false, // balaclava covers face
    muscular: true,
  });
  group.add(body);

  // Tactical vest — more defined
  const vestFront = new THREE.Mesh(
    new THREE.CylinderGeometry(0.21, 0.20, 0.40, SEG, 1, false, -Math.PI * 0.35, Math.PI * 0.7),
    mat(vestColor),
  );
  vestFront.position.set(0, 1.20, -0.06);
  vestFront.rotation.x = Math.PI / 2;
  group.add(vestFront);

  // Vest back
  const vestBack = new THREE.Mesh(
    new THREE.CylinderGeometry(0.20, 0.19, 0.36, SEG, 1, false, Math.PI * 0.5, Math.PI * 0.5),
    mat(vestColor),
  );
  vestBack.position.set(0, 1.20, 0.06);
  vestBack.rotation.x = Math.PI / 2;
  group.add(vestBack);

  // Balaclava eye slit — exposed skin
  const hg0 = fppHeadGroup(group);
  const eyeSlit = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.04, 0.02), matSkin(skinColor));
  eyeSlit.position.set(0, 1.68, -0.135);
  hg0.add(eyeSlit);

  // Eyes visible through slit
  for (const side of [-0.04, 0.04]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.012, 6, 6), mat(0x222222));
    eye.position.set(side, 1.68, -0.145);
    hg0.add(eye);
  }

  // Beanie top
  const beanie = new THREE.Mesh(
    new THREE.SphereGeometry(0.145, SEG, 8, 0, Math.PI * 2, 0, Math.PI * 0.5),
    mat(darken(headwearColor, 0.8)),
  );
  beanie.position.set(0, 1.74, 0);
  hg0.add(beanie);

  // Belt with pouches
  const belt = new THREE.Mesh(new THREE.CylinderGeometry(0.21, 0.20, 0.06, SEG), mat(darken(color, 0.5)));
  belt.position.set(0, 0.88, 0);
  belt.scale.set(1, 1, 0.65);
  group.add(belt);

  for (const side of [-0.16, 0.16]) {
    const pouch = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.07, 0.04), mat(darken(color, 0.55)));
    pouch.position.set(side, 0.88, -0.14);
    group.add(pouch);
  }

  // Backpack
  const backpack = new THREE.Mesh(new THREE.CapsuleGeometry(0.10, 0.12, 4, SEG), mat(darken(vestColor, 0.7)));
  backpack.position.set(0, 1.22, 0.22);
  backpack.scale.set(1.2, 1, 0.6);
  group.add(backpack);

  enableShadows(group);
  return group;
}

/* ------------------------------------------------------------------ */
/*  #1 — Default CT: Military soldier with helmet and goggles          */
/* ------------------------------------------------------------------ */

export function buildDefaultCT(color: number): THREE.Group {
  const group = new THREE.Group();
  const headwearColor = darken(color, 0.4);
  const vestColor = darken(color, 0.85);
  const bootColor = darken(color, 0.3);
  const skinColor = 0xd4b896;

  const body = buildHumanoid({
    bodyColor: color,
    headColor: skinColor,
    skinColor,
    neckColor: skinColor,
    bootColor,
    handColor: bootColor,
    muscular: true,
    eyeColor: 0x4477aa,
  });
  group.add(body);

  // Tactical vest
  const vestFront = new THREE.Mesh(
    new THREE.CylinderGeometry(0.21, 0.20, 0.40, SEG, 1, false, -Math.PI * 0.35, Math.PI * 0.7),
    mat(vestColor),
  );
  vestFront.position.set(0, 1.20, -0.06);
  vestFront.rotation.x = Math.PI / 2;
  group.add(vestFront);

  // Helmet dome — covers upper half of head
  const hg1 = fppHeadGroup(group);
  const helmetDome = new THREE.Mesh(
    new THREE.SphereGeometry(0.175, SEG, 10, 0, Math.PI * 2, 0, Math.PI * 0.58),
    mat(headwearColor),
  );
  helmetDome.position.set(0, 1.74, 0);
  hg1.add(helmetDome);

  // Helmet rim
  const helmetRim = new THREE.Mesh(new THREE.CylinderGeometry(0.175, 0.185, 0.04, SEG), mat(headwearColor));
  helmetRim.position.set(0, 1.74, 0);
  hg1.add(helmetRim);

  // Goggles on helmet
  const goggles = new THREE.Mesh(new THREE.BoxGeometry(0.20, 0.04, 0.04), mat(0x222222));
  goggles.position.set(0, 1.75, -0.14);
  hg1.add(goggles);
  for (const side of [-0.05, 0.05]) {
    const lens = new THREE.Mesh(
      new THREE.CylinderGeometry(0.022, 0.022, 0.02, 8),
      mat(0x5588aa, { roughness: 0.15, metalness: 0.6 }),
    );
    lens.position.set(side, 1.75, -0.16);
    lens.rotation.x = Math.PI / 2;
    hg1.add(lens);
  }

  // Face mask (lower face)
  const mask = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.05, 0.04), mat(darken(color, 0.5)));
  mask.position.set(0, 1.58, -0.12);
  hg1.add(mask);

  // Belt
  const belt = new THREE.Mesh(new THREE.CylinderGeometry(0.21, 0.20, 0.06, SEG), mat(darken(color, 0.5)));
  belt.position.set(0, 0.88, 0);
  belt.scale.set(1, 1, 0.65);
  group.add(belt);

  // Backpack
  const backpack = new THREE.Mesh(new THREE.CapsuleGeometry(0.10, 0.12, 4, SEG), mat(darken(vestColor, 0.7)));
  backpack.position.set(0, 1.22, 0.22);
  backpack.scale.set(1.2, 1, 0.6);
  group.add(backpack);

  enableShadows(group);
  return group;
}
